import { useCallback, useRef, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

/* ------------------------------------------------------------------ */
/*  useClinicalExtraction (PRD.271)                                   */
/*                                                                    */
/*  Calls the `pextractClinicalFromTranscript` Cloud Function with the */
/*  full raw transcript. The function performs the (Siksha) extract-   */
/*  clinical call server-side, validates + de-dupes, persists the      */
/*  `visit_clinical_extractions` row, and returns a slim summary.      */
/*                                                                    */
/*  React NEVER calls the external endpoint directly. This hook owns   */
/*  transient request state only — the read-only extraction panel      */
/*  reads its data from the PowerSync mirror once the row lands.        */
/* ------------------------------------------------------------------ */

const EXTRACT_URL = import.meta.env.VITE_CLINICAL_EXTRACT_URL;

export type ExtractionStatus = 'idle' | 'submitting' | 'success' | 'error';

export interface ExtractionRequest {
  orgId: string;
  patientId: string;
  visitId: string;
  sectionKey: 'chief_complaint';
  recordingId?: string;
  /** Complete raw transcript (all active segments concatenated). */
  transcript: string;
}

export interface ExtractionSummary {
  extractionId: string;
  status: 'completed';
  summary: string;
  symptoms: string[];
  diagnoses: string[];
  allergies: string[];
  cached: boolean;
}

export interface UseClinicalExtractionReturn {
  status: ExtractionStatus;
  error: string | null;
  /** True when the failure is safe to retry (network/timeout/429/5xx). */
  retryable: boolean;
  lastResult: ExtractionSummary | null;
  /** True when the function is reachable (VITE_CLINICAL_EXTRACT_URL configured). */
  enabled: boolean;
  extract: (input: ExtractionRequest) => Promise<ExtractionSummary | null>;
  cancel: () => void;
  reset: () => void;
}

export function useClinicalExtraction(): UseClinicalExtractionReturn {
  const [status, setStatus] = useState<ExtractionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [lastResult, setLastResult] = useState<ExtractionSummary | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const enabled = Boolean(EXTRACT_URL);

  const extract = useCallback(
    async (input: ExtractionRequest): Promise<ExtractionSummary | null> => {
      if (!EXTRACT_URL) {
        setError('Clinical extraction endpoint not configured.');
        setRetryable(false);
        setStatus('error');
        return null;
      }
      if (!input.orgId || !input.patientId || !input.visitId) {
        setError('Missing visit context.');
        setRetryable(false);
        setStatus('error');
        return null;
      }
      if (!input.transcript.trim()) {
        setError('Transcript is empty.');
        setRetryable(false);
        setStatus('error');
        return null;
      }

      setStatus('submitting');
      setError(null);
      setRetryable(false);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = await getIdToken();
        const res = await fetch(EXTRACT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            orgId: input.orgId,
            patientId: input.patientId,
            visitId: input.visitId,
            sectionKey: input.sectionKey,
            ...(input.recordingId ? { recordingId: input.recordingId } : {}),
            transcript: input.transcript,
          }),
          signal: controller.signal,
        });

        const body: unknown = await res.json().catch(() => null);

        if (!res.ok) {
          const asObj = (typeof body === 'object' && body !== null ? body : {}) as Record<
            string,
            unknown
          >;
          const message =
            (typeof asObj.errorMessage === 'string' && asObj.errorMessage) ||
            (typeof asObj.error === 'string' && asObj.error) ||
            `Clinical extraction failed (HTTP ${res.status}).`;
          // The server marks whether a retry is worthwhile; 422/4xx are not.
          setRetryable(asObj.retryable === true);
          setError(message);
          setStatus('error');
          return null;
        }

        const summary = body as ExtractionSummary;
        setLastResult(summary);
        setStatus('success');
        return summary;
      } catch (err) {
        const isAbort =
          (err instanceof Error && (err.name === 'AbortError' || err.name === 'TimeoutError')) ||
          controller.signal.aborted;
        if (isAbort) {
          setStatus('idle');
          setError(null);
          return null;
        }
        // Network-level failure — safe to retry.
        setError(err instanceof Error ? err.message : 'Clinical extraction failed.');
        setRetryable(true);
        setStatus('error');
        return null;
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setStatus('idle');
    setError(null);
    setRetryable(false);
    setLastResult(null);
  }, []);

  return { status, error, retryable, lastResult, enabled, extract, cancel, reset };
}
